import { useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Box, Divider, Flex, Icon, IconButton, List, ListItem, Loader, ModalHeader, Search, Text } from '@vibe/core';
import { Alert as AlertIcon, Board as BoardIcon, Workspace as WorkspaceIcon, Add as AddIcon } from '@vibe/icons';
import fuzzysort from 'fuzzysort';
import { type BoardBasic } from '@gorilla/common/src/lib/monday-api/api';
import { AppModal, AppModalContent, AppModalFooter } from './AppModal';

const MAX_RESULTS = 25;
// Footer height plus its top margin, reserved when a footer is shown.
const FOOTER_HEIGHT = 64;
// Below this viewport height the footer is hidden to leave room for the results.
const MIN_HEIGHT_FOR_FOOTER = 500;

// Each app loads its boards differently (the spreadsheet app also resolves folders), so the caller passes its hook.
// It is only called while the modal is shown, so boards are fetched on first open.
export type UseBoards = () => { boards?: BoardBasic[]; error?: unknown };

// List clones its direct children with keyboard-navigation props (tabIndex, id, ref, role), so results must be direct
// children (no Fragment, no Divider in between; the rows draw their own divider) and pass those props on to ListItem.
type BoardResultProps = Omit<ComponentProps<typeof ListItem>, 'title'> & {
  board: BoardBasic;
  title: ReactNode;
  onSelect: () => void;
};

function BoardResult({ board, title, onSelect, ...listItemProps }: BoardResultProps) {
  // The add button has no handler of its own: its click bubbles up to the ListItem, which selects the board once.
  return (
    <ListItem {...listItemProps} size="large" onClick={onSelect} className="board-picker-result">
      <div style={{ position: 'relative', width: '100%' }}>
        <Flex align="start" justify="start" direction="column" gap={2}>
          <Flex gap={6}>
            <Icon icon={BoardIcon} />
            <div style={{ overflow: 'hidden', maxWidth: '300px', textOverflow: 'ellipsis' }}>
              <strong>{title}</strong>
            </div>
          </Flex>
          <Flex gap={6} style={{ opacity: 0.5 }}>
            <Icon icon={WorkspaceIcon} />
            <div style={{ overflow: 'hidden', maxWidth: '300px', textOverflow: 'ellipsis' }}>
              {board.workspace ? board.workspace.name : 'Main Workspace'}
            </div>
          </Flex>
        </Flex>
        <div style={{ position: 'absolute', top: '50%', right: 0, transform: 'translateY(-50%)' }}>
          <IconButton icon={AddIcon} kind="primary" size="xs" aria-label="Add board" />
        </div>
      </div>
    </ListItem>
  );
}

function CenteredMessage({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
      <div style={{ pointerEvents: 'none', textAlign: 'center' }}>
        <Flex direction="column" gap={10}>
          {children}
        </Flex>
      </div>
    </div>
  );
}

type SingleBoardPickerProps = {
  useBoards: UseBoards;
  searchPlaceholder: string;
  // Space the rest of the modal takes up, so the picker fills it to 90vh.
  reservedHeight: number;
  onBoardSelect: (boardId: number) => void;
};

function SingleBoardPicker({ useBoards, searchPlaceholder, reservedHeight, onBoardSelect }: SingleBoardPickerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const { boards, error } = useBoards();

  const results = useMemo(() => {
    if (!boards || !searchTerm) {
      return [];
    }

    return fuzzysort.go(searchTerm, boards, { keys: ['name'] }).slice(0, MAX_RESULTS);
  }, [searchTerm, boards]);

  const height = `calc(90vh - ${reservedHeight}px)`;

  if (error) {
    return (
      <div style={{ height }}>
        <CenteredMessage>
          <Icon icon={AlertIcon} size={20} />
          <Text type="text2" color="secondary">
            Loading failed
          </Text>
        </CenteredMessage>
      </div>
    );
  }

  if (!boards) {
    return (
      <div style={{ height }}>
        <CenteredMessage>
          <Loader size={20} />
          <Text type="text2" color="secondary">
            Loading boards
          </Text>
        </CenteredMessage>
      </div>
    );
  }

  let resultsContent: ReactNode;

  if (!searchTerm) {
    const firstBoards = boards.slice(0, MAX_RESULTS);
    resultsContent = (
      <List>
        {firstBoards.map((board) => (
          <BoardResult key={board.id} board={board} title={board.name} onSelect={() => onBoardSelect(board.id)} />
        ))}
      </List>
    );
  } else if (!results.length) {
    resultsContent = (
      <CenteredMessage>
        <Icon icon={BoardIcon} size={20} />
        <Text type="text2" color="secondary">
          No boards found
        </Text>
      </CenteredMessage>
    );
  } else {
    resultsContent = (
      <List>
        {results.map((result) => (
          <BoardResult
            key={result.obj.id}
            board={result.obj}
            title={result[0].highlight((match, i) => (
              // The title is already bold, so the matched characters stand out by colour.
              <span key={i} style={{ color: 'var(--primary-color)' }}>
                {match}
              </span>
            ))}
            onSelect={() => onBoardSelect(result.obj.id)}
          />
        ))}
      </List>
    );
  }

  return (
    <div style={{ height, border: '1px solid #d0d4e4', borderRadius: '8px', overflow: 'hidden' }}>
      <div style={{ padding: '20px', backgroundColor: '#f6f7fb' }}>
        <Search placeholder={searchPlaceholder} size="small" value={searchTerm} onChange={setSearchTerm} autoFocus />
      </div>
      <Divider withoutMargin />
      {/* 80px: the search panel above. */}
      <div style={{ overflowY: 'auto', height: `calc(90vh - ${reservedHeight + 80}px)` }}>{resultsContent}</div>
    </div>
  );
}

function useViewportHeight() {
  const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return viewportHeight;
}

type BoardPickerModalProps = {
  show: boolean;
  // Called with the picked board's id, or without one when the modal is dismissed.
  onClose: (boardId?: number) => void;
  useBoards: UseBoards;
  searchPlaceholder?: string;
  // Shown below the picker, hidden on short viewports.
  footer?: ReactNode;
};

export function BoardPickerModal(props: BoardPickerModalProps) {
  if (!props.show) {
    return null;
  }

  return <ShownBoardPickerModal {...props} />;
}

function ShownBoardPickerModal({ show, onClose, useBoards, searchPlaceholder = 'Search board name', footer }: BoardPickerModalProps) {
  const viewportHeight = useViewportHeight();
  const showFooter = !!footer && viewportHeight >= MIN_HEIGHT_FOR_FOOTER;
  // Modal padding, header and the margins around the picker.
  const reservedHeight = 210 + (showFooter ? FOOTER_HEIGHT : 0);

  return (
    <AppModal show={show} onClose={() => onClose()} width="default" alertDialog={false} data-testid="board-picker-modal">
      <ModalHeader title="Add a board" />
      <AppModalContent>
        <Box marginTop="medium" marginBottom="small">
          <SingleBoardPicker
            useBoards={useBoards}
            searchPlaceholder={searchPlaceholder}
            reservedHeight={reservedHeight}
            onBoardSelect={(boardId) => onClose(boardId)}
          />
        </Box>
      </AppModalContent>
      {showFooter && <AppModalFooter>{footer}</AppModalFooter>}
    </AppModal>
  );
}
